import React from "react";
import { categoryOf } from "../../../Utils/partnerFiles";

/*
  The small icon at the head of each row.

  Drawn here rather than loaded, for two reasons: eleven more image
  requests for 16px glyphs is a poor trade, and an inline SVG takes the
  row's own colours without a second asset to keep in step.

  Each is built on the same page outline with a folded corner -- the shape
  a reader already associates with "a file" -- and differs by the colour
  of the fold and one pictograph. At 16px a letter is unreadable, so the
  document types are told apart by colour and the media types by shape,
  which is how a file manager does it at this size.
*/

const PAGE = "M3.5 1.5h5.3L12.5 5v9.5a.5.5 0 0 1-.5.5H3.5a.5.5 0 0 1-.5-.5V2a.5.5 0 0 1 .5-.5z";
const FOLD = "M8.8 1.5 12.5 5H9.3a.5.5 0 0 1-.5-.5z";

/* A page in the family's colour, with its corner turned. */
const Page = ({ tint, children }) => (
  <>
    <path d={PAGE} fill="#ffffff" stroke={tint} strokeWidth="1" />
    <path d={FOLD} fill={tint} opacity="0.35" />
    {children}
  </>
);

const GLYPHS = {
  /* A picture: the hill-and-sun every photo icon uses. */
  image: (
    <Page tint="#1aa1a1">
      <circle cx="5.9" cy="8" r="1" fill="#1aa1a1" />
      <path d="M4 12.5l2.2-2.6 1.3 1.5 1.5-2 2.5 3.1z" fill="#1aa1a1" />
    </Page>
  ),
  /* A film: a play triangle, which reads at any size. */
  video: (
    <Page tint="#7048c4">
      <path d="M6.3 8.2l3.6 2.1-3.6 2.1z" fill="#7048c4" />
    </Page>
  ),
  /* Sound: a single note. */
  audio: (
    <Page tint="#c2447a">
      <path d="M10 7.2v4.1a1.3 1.3 0 1 1-.9-1.23V8.3l-2.5.6v3.1a1.3 1.3 0 1 1-.9-1.23V8.2z" fill="#c2447a" />
    </Page>
  ),
  pdf: (
    <Page tint="#c0392b">
      <rect x="4.6" y="8.6" width="6.2" height="1.1" rx="0.55" fill="#c0392b" />
      <rect x="4.6" y="10.7" width="4.2" height="1.1" rx="0.55" fill="#c0392b" />
    </Page>
  ),
  word: (
    <Page tint="#2b579a">
      <rect x="4.6" y="8.2" width="6.2" height="1" rx="0.5" fill="#2b579a" />
      <rect x="4.6" y="10" width="6.2" height="1" rx="0.5" fill="#2b579a" />
      <rect x="4.6" y="11.8" width="3.6" height="1" rx="0.5" fill="#2b579a" />
    </Page>
  ),
  /* A sheet: a grid, which is what a spreadsheet looks like. */
  excel: (
    <Page tint="#217346">
      <path
        d="M4.5 8.4h6.4v4.4H4.5z"
        fill="none"
        stroke="#217346"
        strokeWidth="0.9"
      />
      <path d="M7.7 8.4v4.4M4.5 10.6h6.4" stroke="#217346" strokeWidth="0.9" />
    </Page>
  ),
  /* A deck: a slide. */
  powerpoint: (
    <Page tint="#c55a11">
      <rect
        x="4.6"
        y="8.5"
        width="6.2"
        height="4.2"
        rx="0.5"
        fill="none"
        stroke="#c55a11"
        strokeWidth="0.9"
      />
      <rect x="5.6" y="9.6" width="4.2" height="1.1" fill="#c55a11" />
    </Page>
  ),
  /* An archive: the zip's teeth. */
  archive: (
    <Page tint="#b8860b">
      <path
        d="M7.2 7.4h1.4v1.2H7.2zM7.2 9.8h1.4V11H7.2z"
        fill="#b8860b"
      />
      <rect x="6.9" y="11.9" width="2" height="1.6" rx="0.5" fill="#b8860b" />
    </Page>
  ),
  text: (
    <Page tint="#6b7684">
      <rect x="4.6" y="8.4" width="6.2" height="0.9" rx="0.45" fill="#6b7684" />
      <rect x="4.6" y="10.1" width="6.2" height="0.9" rx="0.45" fill="#6b7684" />
      <rect x="4.6" y="11.8" width="4" height="0.9" rx="0.45" fill="#6b7684" />
    </Page>
  ),
  file: <Page tint="#9aa5b1" />,
};

export const FolderIcon = ({ className }) => (
  <svg className={className} viewBox="0 0 16 16" aria-hidden="true">
    {/* The back of the folder, so the tab reads as a tab. */}
    <path d="M1.5 4a1 1 0 0 1 1-1h3.1l1.3 1.6H13a1 1 0 0 1 1 1v6.9a1 1 0 0 1-1 1H2.5a1 1 0 0 1-1-1z" fill="#e8b84b" />
    <path d="M1.5 6.1h12.9v6.3a1 1 0 0 1-1 1H2.5a1 1 0 0 1-1-1z" fill="#f7cf72" />
  </svg>
);

export default function FileTypeIcon({ name, className }) {
  return (
    <svg className={className} viewBox="0 0 16 16" aria-hidden="true">
      {GLYPHS[categoryOf(name)] || GLYPHS.file}
    </svg>
  );
}
